import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';
import type z from 'zod';

import { Alert, AlertDescription, AlertTitle } from '@nesso/ui/primitives/alert';
import { TextareaField } from '@nesso/ui/primitives/textarea-field';

import {
    MAX_WHY_LENGTH,
    type StepProps,
    useUpdateInsightsOptions,
    useUpdateOnboardingStepOptions,
    whyInputSchema,
} from '@/entities/practice-member';
import { BulletList } from '@/shared/ui/forms/bullet-list';
import { CardFormSection } from '@/shared/ui/forms/card-form-section';

const WHY_EXAMPLES = [
    'If I don’t have my notes hanging over my head, I feel more confident as a therapist',
    'When my notes are handled, I can enjoy my family instead of thinking about unfinished work',
    'Finishing my notes means I’m not carrying work into the rest of my life',
] as const;

export function OnboardingWhyStep(props: StepProps) {
    const form = useForm<z.infer<typeof whyInputSchema>>({
        mode: 'onChange',
        resolver: zodResolver(whyInputSchema),
        defaultValues: {
            type: 'why',
            customValue: props.stepData?.why?.customValue ?? '',
        },
    });
    const {
        formState: { isDirty, isSubmitting },
    } = form;

    const mutation = useMutation(useUpdateInsightsOptions());
    const updateOnboardingStepMutation = useMutation(useUpdateOnboardingStepOptions());

    const onSubmit = async (data: z.infer<typeof whyInputSchema>) => {
        try {
            if (props.nextStep) {
                updateOnboardingStepMutation.mutate(props.nextStep);
            }
            if (isDirty) {
                await mutation.mutateAsync(data);
            }
            props.onNext();
        } catch {
            console.error('Failed to update why step');
        }
    };

    const handleManualSubmit = () => {
        form.handleSubmit(onSubmit)();
    };

    return (
        <CardFormSection
            title="Why is it important for you to feel on top of your notes?"
            description="Sometimes starting your notes can be the hardest part—remembering the why can help."
            cardTitleClassName="leading-9.5 sm:leading-10.5"
            isFatalError={mutation.isError}
            progressValue={props.progress}
            disabledBackButton={isSubmitting}
            disabledNextButton={isSubmitting}
            onNext={handleManualSubmit}
            onBack={props.onBack}
        >
            <form onSubmit={form.handleSubmit(onSubmit)}>
                <div className="flex flex-col gap-6 sm:gap-10">
                    <Controller
                        name="customValue"
                        control={form.control}
                        render={({ field, fieldState }) => (
                            <TextareaField
                                {...field}
                                id={field.name}
                                label="What’s your why?"
                                disabled={isSubmitting}
                                maxLength={MAX_WHY_LENGTH}
                                className="min-h-[4.9375rem]"
                                fieldProps={{ className: 'sm:max-w-[27rem]' }}
                                error={fieldState.error?.message ?? fieldState.invalid}
                            />
                        )}
                    />
                    <Alert variant="accent" className="py-4 px-2 sm:max-w-[39rem]">
                        <AlertTitle className="text-sm">
                            Some examples we’ve heard from other clinicians:
                        </AlertTitle>
                        <AlertDescription className="pt-1 pl-1 text-sm">
                            <BulletList items={WHY_EXAMPLES} />
                        </AlertDescription>
                    </Alert>
                </div>
            </form>
        </CardFormSection>
    );
}
